import React, { useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef } from "react";

import { useResizeObserver } from "../../hooks/useResizeObserver";
import { scrollIntoView } from "../../utils/dom";

import "./scrollable.css";

/** Describes the result of a useRef on Scrollable */
export interface IScrollableRef {
	scrollIntoView(
		index: number,
		options?: {
			animate?: boolean;
		},
	): void;
}

/**
 * A component with scrollable content
 * When scrolled, the sides will have a fade effect (set fadeDistance to 0 to disable)
 * To be able to fade to the background, a CSS variable with the background color can be set (--scrollable-background-rgb: default is white)
 */
export const Scrollable = ({
	children,
	className,
	containerClassName,
	fadeEnd = false,
	fadeDistance = 40,
	defaultScrollIntoViewMargin = 40,
	orientation = "horizontal",
	ref,
}: React.PropsWithChildren<{
	/** Fade end side when scrollable */
	fadeEnd?: boolean;
	/** The scroll distance that will influence opacity */
	fadeDistance?: number;
	/** Extra classes used on component */
	className?: string;
	/** Class place on the container around this child elements */
	containerClassName?: string;
	/** The default distance to stay from the side when scrolling something into view */
	defaultScrollIntoViewMargin?: number;
	/** Orientation of the scrollable area */
	orientation?: "horizontal" | "vertical";
	ref?: React.Ref<IScrollableRef>;
}>) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const fadeEndRef = useRef<HTMLDivElement>(null);

	const update = useCallback(() => {
		const containerEl = containerRef.current;
		if (!containerEl) return;

		const fadeEndEl = fadeEndRef.current;
		if (!fadeEndEl) return;

		if (orientation === "horizontal") {
			const scrollDistanceRight =
				containerEl.scrollWidth - containerEl.clientWidth - containerEl.scrollLeft;
			fadeEndEl.style.opacity =
				fadeEnd && fadeDistance
					? `${Math.min(0.95, scrollDistanceRight / fadeDistance)}`
					: "0";
		} else {
			const scrollDistanceBottom =
				containerEl.scrollHeight - containerEl.clientHeight - containerEl.scrollTop;
			fadeEndEl.style.opacity =
				fadeEnd && fadeDistance
					? `${Math.min(0.95, scrollDistanceBottom / fadeDistance)}`
					: "0";
		}
	}, [orientation, fadeEnd, fadeDistance]);

	// With useImperativeHandle we can give control to the parent, to scrollIntoView if the selected zone changes
	useImperativeHandle(
		ref,
		() => ({
			/** Used index instead of 'id' because item can also be an element */
			scrollIntoView: (
				index: number,
				{
					animate = true,
					margins = eXpress.math.margins(defaultScrollIntoViewMargin),
				}: {
					animate?: boolean;
					margins?: eXpress.math.IMargins;
				} = {},
			) => {
				if (!containerRef.current) return;
				const selectedEl = containerRef.current.children[index];
				if (!selectedEl) return;

				// Didn't use native scrollIntoView because it will not work when multiple menu's must scroll
				scrollIntoView(selectedEl, {
					margins,
					animate,
				});
			},
		}),
		[defaultScrollIntoViewMargin],
	);

	// Update when created or props changed
	useLayoutEffect(update, [update]);

	// Update UI when size changes
	useResizeObserver(containerRef, update, !fadeEnd);

	// Try to detect changed children. (needed when component size doesn't change)
	// - Idea 1: Watch size of each child with ResizeObserver -> Doesn't work well as elements can be added/removed
	// - Idea 2: Adding one child and use resize observer on it -> no scrollIntoView possible because 1 child.
	// - Idea 3: Use MutationObserver to keep list up-to-date seems to work
	// - Idea 4: Expose the update function and let caller update this when needed -> responsibiliy to caller, seems to be more error prone
	// I did use Idea 3 here
	useEffect(() => {
		const containerEl = containerRef.current;
		if (!containerEl || !fadeEnd) return undefined;

		const observer = new MutationObserver(update);

		observer.observe(containerEl, {
			childList: true,
			attributes: true,
			subtree: true,
		});

		return () => {
			observer.disconnect();
		};
	}, [update, fadeEnd]);

	return (
		<div className={`scrollable scrollable--${orientation} ${className ?? ""}`}>
			<div
				ref={containerRef}
				className={`scrollable__container ${containerClassName ?? ""}`}
				onScroll={update}
			>
				{children}
			</div>
			<div className="scrollable__fade-right" ref={fadeEndRef} />
		</div>
	);
};
Scrollable.displayName = "Scrollable";
